/** @jsxImportSource ../../i18n */
import React, { useEffect, useState } from 'react';
import { CloudDrizzle, CloudLightning, CloudRain, CloudSun, Sun, Umbrella } from 'lucide-react';
import api from '../../services/api';
import { cx } from '../../theme/cx';
import { formatDate, formatNumber } from '../../theme/format';
import { Badge, Card, CardHeader, Dialog, Money, Percent, Skeleton, Table, TBody, Td, Th, THead, Tr } from '../ui';

// Weather of a scenario from its estimated impact on the whole account.
export const weatherOf = (pct) => {
  if (pct >= 0) return { icon: Sun, label: 'Éclaircie', tone: 'text-gain-400' };
  if (pct > -3) return { icon: CloudSun, label: 'Voilé', tone: 'text-fg-muted' };
  if (pct > -8) return { icon: CloudDrizzle, label: 'Averses', tone: 'text-warn-300' };
  if (pct > -15) return { icon: CloudRain, label: 'Pluie', tone: 'text-warn-400' };
  return { icon: CloudLightning, label: 'Orage', tone: 'text-loss-400' };
};

const ScenarioDetail = ({ scenario, onClose }) => (
  <Dialog open={Boolean(scenario)} onClose={onClose} title={scenario?.label} description={scenario && `${scenario.description} Sensibilité de chaque ligne à : ${scenario.driver}.`} size="lg">
    {scenario && (
      <div className="-mx-1 max-h-[60vh] overflow-y-auto px-1">
      <Table>
        <THead>
          <tr>
            <Th>Valeur</Th>
            <Th align="right">Sensibilité</Th>
            <Th align="right">Impact</Th>
          </tr>
        </THead>
        <TBody>
          {scenario.lines.map((l) => (
            <Tr key={l.symbol}>
              <Td className="w-full max-w-0">
                <p className="truncate font-medium text-fg">{l.symbol}</p>
                <p className="truncate text-xs text-fg-subtle">{l.description}</p>
              </Td>
              <Td align="right" numeric className="whitespace-nowrap">
                {formatNumber(l.sensitivity, { decimals: 2 })}
                {l.method === 'default' && <Badge className="ml-2">par défaut</Badge>}
              </Td>
              <Td align="right" className="whitespace-nowrap">
                <Money value={l.impact_value} decimals={0} sign colorize className="block font-medium" />
                <Percent value={l.impact_pct} colorize className="text-xs" />
              </Td>
            </Tr>
          ))}
        </TBody>
      </Table>
      </div>
    )}
  </Dialog>
);

// Dashboard card: the portfolio under four scenarios, as a weather forecast.
export const WeatherCard = () => {
  const [data, setData] = useState(undefined);
  const [open, setOpen] = useState(null);

  useEffect(() => {
    let alive = true;
    api.get('/portfolio/stress-test')
      .then((res) => { if (alive) setData(Array.isArray(res?.data?.scenarios) ? res.data : null); })
      .catch(() => { if (alive) setData(null); });
    return () => { alive = false; };
  }, []);

  if (data === null) return null;
  return (
    <Card data-testid="weather-card">
      <CardHeader
        icon={Umbrella}
        title="Météo du portefeuille"
        description="Impact estimé de quatre scénarios, d’après la sensibilité historique de vos lignes sur un an."
      />
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {data === undefined
          ? [0, 1, 2, 3].map((i) => <div key={i} className="rounded-control border border-line p-4" aria-hidden="true"><Skeleton className="h-8 w-8 rounded-full" /><Skeleton className="mt-4 h-3 w-24" /><Skeleton className="mt-3 h-6 w-20" /></div>)
          : data.scenarios.map((sc) => {
            const w = weatherOf(sc.impact_pct);
            const Icon = w.icon;
            return (
              <button
                key={sc.id}
                type="button"
                onClick={() => setOpen(sc)}
                data-testid={`weather-${sc.id}`}
                className="group flex flex-col items-start rounded-control border border-line bg-surface-2/40 p-4 text-left transition-colors hover:border-line-strong hover:bg-surface-2"
              >
                <span className="flex w-full items-center justify-between gap-3">
                  <Icon size={28} strokeWidth={1.25} className={cx('flex-shrink-0', w.tone)} aria-hidden="true" />
                  <span className="text-xs text-fg-subtle">{w.label}</span>
                </span>
                <span className="mt-3 font-medium text-fg">{sc.label}</span>
                <span className="mt-0.5 text-xs leading-relaxed text-fg-muted">{sc.description}</span>
                <span className="mt-3 flex items-baseline gap-2">
                  <Money value={sc.impact_value} decimals={0} sign colorize className="font-display text-xl" />
                  <Percent value={sc.impact_pct} colorize className="text-sm" />
                </span>
                <span className="mt-2 text-xs font-medium text-gold-300 opacity-80 group-hover:opacity-100">Détail par ligne</span>
              </button>
            );
          })}
      </div>
      {data && (
        <p className="mt-4 text-xs leading-relaxed text-fg-subtle">
          Estimation, pas une prévision : sensibilités mesurées du {formatDate(data.from)} au {formatDate(data.to)}, liquidités inchangées
          {data.unavailable?.length ? ` ; historique indisponible pour ${data.unavailable.join(', ')} (sensibilité par défaut).` : '.'}
        </p>
      )}
      <ScenarioDetail scenario={open} onClose={() => setOpen(null)} />
    </Card>
  );
};
